import {
  Add,
  Archive,
  KeyboardArrowDown,
  KeyboardArrowRight,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import React, { useEffect, useRef, useState } from "react";
import api from "../../services/api";
import { useWorkspace } from "../../utils/WorkspaceContext";
import useDebounce from "../../hooks/debounceHook";
import DivisionDialog from "../dialogs/DivisionDialog";

const StyledPaper = styled(Paper)(({ theme }) => ({
  margin: theme.spacing(0.1),
  padding: theme.spacing(0.1),
}));

const DepartmentHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(1),
  cursor: "pointer",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const DivisionRow = styled(Grid)(({ theme }) => ({
  alignItems: "center",
  padding: theme.spacing(1),
  marginLeft: theme.spacing(3),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const HeaderRow = styled(Grid)(({ theme }) => ({
  padding: theme.spacing(1),
  marginLeft: theme.spacing(3),
  borderBottom: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.secondary,
  fontWeight: "bold",
  alignItems: "center",
}));

const DivisionListView = () => {
  const [divisions, setDivisions] = useState([]);
  const [departments, setDepartments] = useState([]);
  // Unsaved blank rows the user is filling in, keyed by a temp id. They are
  // only persisted once a name is entered and focus leaves the row.
  const [drafts, setDrafts] = useState([]);
  const [expandedDepartments, setExpandedDepartments] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const { currentWorkspace } = useWorkspace();

  // Latest drafts + in-flight guard, read inside async blur handlers without
  // stale-closure issues / double submits.
  const draftsRef = useRef([]);
  useEffect(() => {
    draftsRef.current = drafts;
  }, [drafts]);
  const committing = useRef(new Set());

  const [snackbarState, setSnackbarState] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  const [contextMenu, setContextMenu] = useState(null);
  const [selectedDivision, setSelectedDivision] = useState(null);

  const showSnackbar = (message, severity = "error") => {
    setSnackbarState({ open: true, message, severity });
  };

  const handleSnackbarClose = () => {
    setSnackbarState((prev) => ({ ...prev, open: false }));
  };

  const fetchDivisions = async () => {
    try {
      const response = await api.get("divisions/", {
        params: { workspace_id: currentWorkspace.id },
      });
      setDivisions(response.data);
    } catch (error) {
      console.error("Error fetching divisions:", error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await api.get("departments/", {
        params: { workspace_id: currentWorkspace.id },
      });
      setDepartments(response.data);
    } catch (error) {
      console.error("Error fetching departments:", error);
    }
  };

  useEffect(() => {
    fetchDivisions();
    fetchDepartments();
  }, [currentWorkspace.id]);

  // Group divisions under their parent department entity.
  const groupedByDepartment = departments.map((dept) => ({
    code: dept.id,
    label: dept.name,
    divisions: divisions.filter((d) => d.department === dept.id),
  }));

  const toggleDepartment = (code) => {
    setExpandedDepartments((prev) => ({ ...prev, [code]: !prev[code] }));
  };

  const debouncedUpdateDivision = useDebounce(async (divisionId, updates) => {
    try {
      await api.patch(`divisions/${divisionId}/`, {
        ...updates,
        workspace: currentWorkspace.id,
      });
    } catch (error) {
      console.error("Error updating division:", error, "Updates:", updates);
    }
  });

  const handleDivisionUpdate = (divisionId, field, value) => {
    // Optimistic UI update.
    setDivisions((prev) =>
      prev.map((division) =>
        division.id === divisionId ? { ...division, [field]: value } : division
      )
    );
    debouncedUpdateDivision(divisionId, { [field]: value });
  };

  // Inline add: drop a blank, unsaved row under the department. The user fills
  // it in; it's only persisted on blur if they entered a name. Only the
  // top-level button uses the dialog.
  const handleAddDivision = (departmentCode) => {
    setExpandedDepartments((prev) => ({ ...prev, [departmentCode]: true }));
    setDrafts((prev) => [
      ...prev,
      {
        tempId: `draft-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        department: departmentCode,
        name: "",
        description: "",
      },
    ]);
  };

  const updateDraft = (tempId, field, value) => {
    setDrafts((prev) =>
      prev.map((draft) =>
        draft.tempId === tempId ? { ...draft, [field]: value } : draft
      )
    );
  };

  const removeDraft = (tempId) => {
    setDrafts((prev) => prev.filter((draft) => draft.tempId !== tempId));
  };

  // Called when focus leaves a draft row. Discard if still blank, otherwise
  // create the division.
  const commitDraft = async (tempId) => {
    const draft = draftsRef.current.find((d) => d.tempId === tempId);
    if (!draft || committing.current.has(tempId)) return;

    if (!draft.name.trim()) {
      removeDraft(tempId);
      return;
    }

    committing.current.add(tempId);
    try {
      await api.post("divisions/", {
        name: draft.name.trim(),
        description: draft.description,
        department: draft.department,
        workspace: currentWorkspace.id,
      });
      removeDraft(tempId);
      fetchDivisions();
    } catch (error) {
      showSnackbar("Error creating division.", "error");
      console.error("Error creating division:", error);
    } finally {
      committing.current.delete(tempId);
    }
  };

  const handleContextMenu = (event, division) => {
    event.preventDefault();
    setContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
    });
    setSelectedDivision(division);
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
    setSelectedDivision(null);
  };

  const handleArchiveDivision = async () => {
    if (!selectedDivision?.id) {
      handleCloseContextMenu();
      return;
    }
    try {
      await api.delete(`divisions/${selectedDivision.id}/`, {
        params: { workspace_id: currentWorkspace.id },
      });
      fetchDivisions();
      showSnackbar("Division archived successfully.", "success");
    } catch (error) {
      const detail = error?.response?.data?.detail || "Error archiving division.";
      showSnackbar(detail, "error");
      console.error("Error archiving division:", error);
    }
    handleCloseContextMenu();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box />
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setDialogOpen(true)}
        >
          Create Division
        </Button>
      </Stack>

      <StyledPaper elevation={1}>
        {groupedByDepartment.map((department) => (
          <Box key={department.code}>
            <DepartmentHeader onClick={() => toggleDepartment(department.code)}>
              {expandedDepartments[department.code] ? (
                <KeyboardArrowDown />
              ) : (
                <KeyboardArrowRight />
              )}
              <Typography variant="h6">{department.label}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                ({department.divisions.length})
              </Typography>
            </DepartmentHeader>

            {expandedDepartments[department.code] && (
              <>
                <HeaderRow container spacing={2}>
                  <Grid item size={5}>
                    <Typography>Division Name</Typography>
                  </Grid>
                  <Grid item size={7}>
                    <Typography>Description</Typography>
                  </Grid>
                </HeaderRow>

                {department.divisions.map((division) => (
                  <DivisionRow
                    container
                    spacing={2}
                    key={division.id}
                    onContextMenu={(e) => handleContextMenu(e, division)}
                  >
                    <Grid item size={5}>
                      <TextField
                        fullWidth
                        variant="standard"
                        value={division.name}
                        onChange={(e) =>
                          handleDivisionUpdate(division.id, "name", e.target.value)
                        }
                      />
                    </Grid>
                    <Grid item size={7}>
                      <TextField
                        fullWidth
                        variant="standard"
                        value={division.description || ""}
                        onChange={(e) =>
                          handleDivisionUpdate(
                            division.id,
                            "description",
                            e.target.value
                          )
                        }
                      />
                    </Grid>
                  </DivisionRow>
                ))}

                {drafts
                  .filter((draft) => draft.department === department.code)
                  .map((draft) => (
                    <DivisionRow
                      container
                      spacing={2}
                      key={draft.tempId}
                      onBlur={(e) => {
                        // Commit only when focus leaves the whole row (e.g.
                        // moving between name and description should not).
                        if (!e.currentTarget.contains(e.relatedTarget)) {
                          commitDraft(draft.tempId);
                        }
                      }}
                    >
                      <Grid item size={5}>
                        <TextField
                          fullWidth
                          autoFocus
                          variant="standard"
                          placeholder="Division name"
                          value={draft.name}
                          onChange={(e) =>
                            updateDraft(draft.tempId, "name", e.target.value)
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter") e.target.blur();
                            if (e.key === "Escape") removeDraft(draft.tempId);
                          }}
                        />
                      </Grid>
                      <Grid item size={7}>
                        <TextField
                          fullWidth
                          variant="standard"
                          placeholder="Description"
                          value={draft.description}
                          onChange={(e) =>
                            updateDraft(
                              draft.tempId,
                              "description",
                              e.target.value
                            )
                          }
                        />
                      </Grid>
                    </DivisionRow>
                  ))}

                <Button
                  sx={{ mt: 1, ml: 4 }}
                  onClick={() => handleAddDivision(department.code)}
                >
                  + Add Division
                </Button>
              </>
            )}
          </Box>
        ))}
      </StyledPaper>

      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem
          onClick={handleArchiveDivision}
          sx={{
            color: "error.main",
            "&:hover": { bgcolor: "error.light" },
          }}
        >
          <Archive sx={{ mr: 1 }} fontSize="small" />
          Archive Division
        </MenuItem>
      </Menu>

      <DivisionDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSuccess={() => {
          setDialogOpen(false);
          fetchDivisions();
        }}
      />

      <Snackbar
        open={snackbarState.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarState.severity}
          sx={{ width: "100%" }}
        >
          {snackbarState.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DivisionListView;
